import Resources from '@/plugins/sdk/Resources'
import Field from '@/plugins/sdk/Field'
import Request, { GET, POST, PUT, DELETE } from '@/plugins/sdk/Request'
import { CREATING, CREATED, UPDATING, UPDATED, DELETING, DELETED, RESTORING, RESTORED } from '@/plugins/sdk/Events'
import Listener from '@/plugins/sdk/Listener'
import Listeners from '@/plugins/sdk/Listeners'
import Vue from 'vue'
import pickBy from 'lodash/pickBy'
import isPlainObject from 'lodash/isPlainObject'
import UnauthorizedError from './Errors/RequestError/UnauthorizedError'
import ForbiddenError from './Errors/RequestError/ForbiddenError'
import NotFoundError from './Errors/RequestError/NotFoundError'
import RequestError from './Errors/RequestError'
import MethodNotAllowedError from './Errors/RequestError/MethodNotAllowedError'
import BadRequestError from './Errors/RequestError/BadRequestError'

export default class {
  /**
   * Constructs the object.
   *
   * @return     {Proxy}
   */
  constructor () {
    Object.defineProperties(this, {
      $fields: {
        configurable: false,
        enumerable: false,
        value: Object
          .seal(
            pickBy(
              this.constructor.fields,
              function (value) {
                return value instanceof Field
              }
            )
          ),
        writable: false
      },
      modified: {
        configurable: false,
        enumerable: false,
        get: function () {
          let modified = false

          for (let field of Object.keys(this.$fields)) {
            modified = !!this.$fields[field].modified

            if (modified) {
              break
            }
          }

          return modified
        }
      }
    })

    return new Proxy(Object.freeze(this), {
      get (target, property) {
        // if (property === 'toJSON') {
        //   return () => target
        // }
        if (property in target) {
          return target[property]
        } else if (property === 'id') {
          return target.$fields[target.constructor.identifier].current
        } else if (property in target.$fields) {
          return target.$fields[property].current
        }
      },
      set (target, property, value) {
        if (property === 'id') {
          target.$fields[target.constructor.identifier].current = value
        } else if (property in target.$fields) {
          target.$fields[property].current = value
        } else if (property in target) {
          target[property] = value
        }

        return true
      },
      has (target, property) {
        return property in target.$fields
      }
    })
  }

  /**
   * The name of the resource.
   */
  static name = 'resource'

  /**
   * The primary identification field for the resource.
   */
  static identifier = 'id'

  /**
   * The primary label field for the resource.
   */
  static label = 'id'

  /**
   * Get the label field of the resource.
   */
  get label () {
    return this.$fields[this.constructor.label].current
  }

  /**
   * Determines if the resource exists or not.
   */
  get exists () {
    return !!this.$fields[this.constructor.identifier].default
  }

  /**
   * Listen for a given event against the resource.
   *
   * @param      {mixed}     event     The event to listen to
   * @param      {Function}  callback  The callback when an event is triggered
   * @param      {Object}    vm        A Vue vm instance
   */
  static on (event, callback, vm) {
    const listener = new Listener(this, event, callback)

    if (vm instanceof Vue) {
      vm.$once('hook:beforeDestroy', () => {
        listener.destroy()
      })
    }

    return listener
  }

  /**
   * Emit an event from the resource.
   *
   * @param      {mixed}  event   The event
   * @param      {mixed}  data    The data
   */
  static emit (event, data) {
    Listeners.emit(this, event, data)
  }

  /**
   * Saves the resource.
   *
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  async save (callback) {
    if (this.exists) {
      return this.update(callback)
    } else {
      return this.create(callback)
    }
  }

  /**
   * Creates the resource.
   *
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  async create (callback) {
    this.constructor.emit(CREATING, this)

    await this.constructor._request(this, POST, callback)

    this.constructor.emit(CREATED, this)

    return this
  }

  /**
   * Creates a new resource.
   *
   * @param      {Object}    data      The data
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  static async create (data, callback) {
    const resource = new this()

    resource.fill(data)

    return resource.create(callback)
  }

  /**
   * @see refresh
   */
  async read (callback) {
    await this.refresh(callback)

    return this
  }

  /**
   * Refreshes the resource.
   *
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  async refresh (callback) {
    await this.constructor._request(this, GET, callback)

    return this
  }

  /**
   * Reads an existing resource.
   *
   * @param      {mixed}     id        The identifier
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  static async read (id, callback) {
    const resource = new this()

    resource.id = id

    return resource.read(callback)
  }

  /**
   * Lists existing resources.
   *
   * @param      {Function}  callback  A request builder callback
   * @param      {AbortSignal|null} abortSignal
   * @return     {Promise}
   */
  static async list (callback, abortSignal = null) {
    return Resources.list(this, callback, abortSignal)
  }

  /**
   * Updates the resource.
   *
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  async update (callback) {
    this.constructor.emit(UPDATING, this)

    await this.constructor._request(this, PUT, callback)

    this.constructor.emit(UPDATED, this)

    return this
  }

  /**
   * Updates an existing resource.
   *
   * @param      {mixed}     id        The identifier
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  static async update (id, data, callback) {
    const resource = new this()

    resource.fill(Object.assign({}, data, { id }))

    return resource.update(callback)
  }

  /**
   * Deletes the resource.
   *
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  async delete (callback) {
    this.constructor.emit(DELETING, this)

    await this.constructor._request(this, DELETE, callback)

    this.constructor.emit(DELETED)

    return this
  }

  /**
   * Deletes an existing resource.
   *
   * @param      {mixed}     id        The identifier
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  static async delete (id, callback) {
    const resource = new this()

    resource.id = id

    return resource.delete(callback)
  }

  /**
   * Restores the resource.
   *
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  async restore (callback) {
    this.constructor.emit(RESTORING)

    await this.constructor._request(this, POST, callback)

    this.constructor.emit(RESTORED, this)

    return this
  }

  /**
   * Restores an existing resource.
   *
   * @param      {mixed}     id        The identifier
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  static async restore (id, callback) {
    const resource = new this()

    resource.id = id

    return resource.restore(callback)
  }

  /**
   * Performs an API request.
   *
   * @param      {Object}    resource  The resource
   * @param      {String}    method    The HTTP request method
   * @param      {Function}  callback  A request builder callback
   * @return     {Promise}
   */
  static async _request (resource, method, callback) {
    let request = Object.assign(new Request(), {
      body: resource,
      method: method,
      context: resource
    })

    if (typeof callback === 'function') {
      await Promise.resolve(callback(request))
    }

    request = await request.build()

    let response = await fetch(request)

    if (response.ok) {
      response = await response.json()
      response.data = response.data[Object.keys(response.data).shift()]

      if (isPlainObject(response.data)) {
        resource.fill(response.data, true)
        resource.reset()
      }
    } else {
      switch (response.status) {
        case 400:
          const data = (await response.json()).data
          if (data) {
            Object
              .keys(data)
              .map(field => {
                resource
                  .$fields[field]
                  .messages = data[field]
              })
          }
          throw new BadRequestError(resource, request, response, 'Bad Request Error')
        case 401:
          throw new UnauthorizedError(resource, request, response, 'Unauthorized Error')
        case 403:
          throw new ForbiddenError(resource, request, response, 'Permission Denied Error')
        case 404:
          throw new NotFoundError(resource, request, response, 'Resource Not Found Error')
        case 405:
          throw new MethodNotAllowedError(resource, request, response, 'Request method is not allowed')
        default:
          throw new RequestError(resource, request, response, 'An error has occurred while requesting to the server')
      }
    }

    return resource
  }

  /**
   * Fills field values with the given data.
   *
   * @param      {Object}   data      The data
   * @param      {Boolean}  defaults  Set values as default
   */
  fill (data, defaults) {
    Object
      .keys(this.$fields)
      .map(property => {
        if (typeof data[property] !== 'undefined') {
          this.$fields[property][defaults ? 'default' : 'current'] = data[property]
        }
      })
  }

  /**
   * Clears the resource to an initial state.
   */
  clear () {
    Object
      .keys(this.$fields)
      .map(field => { this.$fields[field] = this.constructor.fields[field] })
  }

  /**
   * Resets the resource to its default state.
   */
  reset () {
    Object
      .keys(this.$fields)
      .map(field => this.$fields[field].reset())
  }

  /**
   * Creates a new instance of the object with same properties as the original.
   */
  clone () {
    const resource = new this.constructor()

    Object
      .keys(this.$fields)
      .map(property => {
        resource
          .$fields[property]
          ._current = this
            .$fields[property]
            .mutator(this.$fields[property]._current)

        resource
          .$fields[property]
          ._default = this
            .$fields[property]
            .mutator(this.$fields[property]._default)
      })

    return resource
  }

  /**
   * Creates a new instance of the object with same properties as the original.
   */
  replicate () {
    const resource = new this.constructor()

    Object
      .keys(this.$fields)
      .map(property => {
        resource[property] = this
          .$fields[property]
          .copy()
      })

    // resource.$fields[this.constructor.identifier].reset()

    return resource
  }

  /**
   * @see        {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#toJSON()_behavior}
   * @return     {object}
   */
  toJSON () {
    const resource = {
      [this.constructor.identifier]: this.id
    }

    Object
      .keys(this.$fields)
      .filter(field => this.$fields[field].modified)
      .map(field => { resource[field] = this[field] })

    return resource
  }

  /**
   * @see        {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/toString}
   * @return     {string}
   */
  toString () {
    return this.constructor.name
  }

  /**
   * @see        {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/toString}
   * @return     {string}
   */
  static toString () {
    return this.name
  }
}
